import React, { useEffect, useState } from 'react';
import { connectionInvitationsApi } from '../utils/api';
import InlineNotePanel from './InlineNotePanel';

// PRD-010 F-PI-3 — seller-side Pending Invitations panel.
//
// Surfaces all 'sent' Connection invitations the workspace has open so
// the seller can recover the invite URL (regenerate is the recovery
// path because the original raw token was hashed and is unrecoverable),
// or revoke the invite. Hides itself entirely when there's nothing to
// show.

function timeUntil(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return 'less than 1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

export default function PendingInvitationsPanel({ refreshTrigger = 0 }) {
  const [invitations, setInvitations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Per-row local state: regenerated URLs (in-memory only — never refetched
  // from the server) and the inline-revoke confirmation panel.
  const [regeneratedUrls, setRegeneratedUrls] = useState({});  // {id: url}
  const [busy, setBusy] = useState({});                         // {id: 'regenerate'|'revoke'|undefined}
  const [revokeFor, setRevokeFor] = useState(null);
  const [error, setError] = useState(null);
  const [copiedFor, setCopiedFor] = useState(null);

  async function load() {
    try {
      const r = await connectionInvitationsApi.listSent();
      setInvitations(r.data.invitations || []);
    } catch (e) {
      // Non-fatal — just hide the panel if listing fails.
      console.error('Failed to load pending invitations:', e);
      setInvitations([]);
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => { load(); }, [refreshTrigger]);

  if (!loaded) return null;
  if (invitations.length === 0) return null;

  async function handleRegenerate(id) {
    setError(null);
    setBusy((b) => ({ ...b, [id]: 'regenerate' }));
    try {
      const r = await connectionInvitationsApi.regenerateUrl(id);
      setRegeneratedUrls((u) => ({ ...u, [id]: r.data.inviteUrl }));
      setInvitations((arr) =>
        arr.map((inv) =>
          inv.id === id ? { ...inv, expiresAt: r.data.expiresAt || inv.expiresAt } : inv
        )
      );
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: undefined }));
    }
  }

  async function handleCopy(id) {
    const url = regeneratedUrls[id];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFor(id);
      window.setTimeout(() => setCopiedFor((cur) => (cur === id ? null : cur)), 2000);
    } catch {
      /* clipboard may be blocked; the URL is still visible inline */
    }
  }

  async function submitRevoke() {
    if (!revokeFor) return;
    const id = revokeFor;
    setBusy((b) => ({ ...b, [id]: 'revoke' }));
    try {
      await connectionInvitationsApi.revoke(id);
      setRevokeFor(null);
      setInvitations((arr) => arr.filter((inv) => inv.id !== id));
      setRegeneratedUrls((u) => {
        const { [id]: _drop, ...rest } = u;
        return rest;
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: undefined }));
    }
  }

  return (
    <div className="card p-5 mb-6 border border-amber-200 bg-amber-50/30">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-amber-900">
          Pending invitations sent
        </h2>
        <span className="text-[11px] text-amber-700">{invitations.length} open</span>
      </div>
      {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}
      <ul className="space-y-2">
        {invitations.map((inv) => {
          const url = regeneratedUrls[inv.id];
          const canCopy = !!url;
          const expCountdown = timeUntil(inv.expiresAt);
          return (
            <li
              key={inv.id}
              className="bg-white border border-amber-200 rounded-lg p-3 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    Awaiting acceptance from{' '}
                    <span className="text-gray-800">{inv.invitedEmail}</span>
                    {expCountdown && (
                      <span className="ml-1 text-xs text-amber-700">
                        · expires in {expCountdown}
                      </span>
                    )}
                  </div>
                  {inv.invitedWorkspaceName && (
                    <div className="text-xs text-gray-600 mt-0.5">
                      Workspace: {inv.invitedWorkspaceName}
                    </div>
                  )}
                  {inv.contextNote && (
                    <p className="text-xs text-gray-600 mt-1 italic line-clamp-2">
                      &ldquo;{inv.contextNote}&rdquo;
                    </p>
                  )}
                  <div className="text-[11px] text-gray-600 mt-0.5">
                    Sent {new Date(inv.createdAt).toLocaleDateString()}
                    {' · '}role you offered:{' '}
                    {inv.initiatorRole === 'seller' ? 'buyer' : 'seller'}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    disabled={!canCopy}
                    onClick={() => handleCopy(inv.id)}
                    title={canCopy
                      ? 'Copy the invitation URL to your clipboard'
                      : 'URL is one-time-readable; regenerate to get a new link.'}
                    className="text-xs px-2.5 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {copiedFor === inv.id ? 'Copied' : 'Copy URL'}
                  </button>
                  <button
                    type="button"
                    disabled={busy[inv.id] === 'regenerate'}
                    onClick={() => handleRegenerate(inv.id)}
                    className="text-xs px-2.5 py-1 rounded border border-amber-300 text-amber-900 bg-amber-100 hover:bg-amber-200 disabled:opacity-40"
                  >
                    {busy[inv.id] === 'regenerate' ? 'Regenerating…' : 'Regenerate URL'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRevokeFor(inv.id)}
                    className="text-xs px-2.5 py-1 rounded text-red-700 hover:bg-red-50"
                  >
                    Revoke
                  </button>
                </div>
              </div>

              {url && (
                <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs font-mono break-all text-gray-700">
                  {url}
                </div>
              )}

              {revokeFor === inv.id && (
                <InlineNotePanel
                  title="Revoke this invitation?"
                  description="The recipient will no longer be able to accept it. This cannot be undone."
                  primaryLabel="Revoke invitation"
                  cancelLabel="Cancel"
                  noteLabel="Reason"
                  notePlaceholder="Optional — internal note for audit log."
                  tone="danger"
                  busy={busy[inv.id] === 'revoke'}
                  onSubmit={submitRevoke}
                  onCancel={() => setRevokeFor(null)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
